import React from "react";

/**
 * Reusable PageHeader component providing consistent titles,
 * descriptions, icon styling, and action areas with a modern card background
 * across all admin screens.
 */
const PageHeader = ({
  title,
  description,
  icon: Icon,
  badge,
  actions,
  children,
  className = "",
}) => {
  return (
    <div
      className={`relative overflow-hidden mb-6 rounded-2xl sm:rounded-3xl bg-white dark:bg-gray-800/90 backdrop-blur-md border border-gray-100 dark:border-gray-700/80 shadow-xs p-5 sm:p-6 transition-all ${className}`}
    >
      {/* Decorative ambient background glows */}
      <div className="absolute -top-12 -right-12 w-48 h-48 bg-gradient-to-br from-[#05DAC3]/15 via-teal-500/5 to-transparent rounded-full blur-2xl pointer-events-none" />
      <div className="absolute -bottom-10 -left-10 w-40 h-40 bg-gradient-to-tr from-[#05DAC3]/10 to-transparent rounded-full blur-xl pointer-events-none" />

      <div className="relative z-10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        {/* Left side: Icon + Title & Description */}
        <div className="flex items-start gap-4 min-w-0">
          {Icon && (
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-teal-50 to-teal-100/60 dark:from-teal-950/60 dark:to-teal-900/30 text-[#00B4A6] dark:text-[#05DAC3] border border-teal-200/60 dark:border-teal-800/40 shrink-0 shadow-xs flex items-center justify-center mt-0.5">
              <Icon className="w-6 h-6" />
            </div>
          )}
          <div className="min-w-0">
            <div className="flex items-center gap-2.5 flex-wrap">
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white tracking-tight">
                {title}
              </h1>
              {badge && (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-[#05DAC3]/10 text-[#009688] dark:text-[#05DAC3] border border-[#05DAC3]/25">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#05DAC3] animate-pulse" />
                  {badge}
                </span>
              )}
            </div>
            {description && (
              <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 mt-1 leading-relaxed">
                {description}
              </p>
            )}
          </div>
        </div>

        {/* Right side: Action Controls */}
        {(actions || children) && (
          <div className="flex items-center gap-3 shrink-0 flex-wrap sm:self-center">
            {actions || children}
          </div>
        )}
      </div>
    </div>
  );
};

export default PageHeader;
